import type { VersionedDraftKit } from '@/lib/versioned-draft';
import type {
  NoyauCode,
  NoyauTextEntryDraft,
  NoyauTextsContent,
  NoyauTextsDetail,
  NoyauTextsDraftContent,
  RichText,
} from '@matter_extractor/shared';
import {
  NOYAU_CODES,
  noyauTextFilled,
  noyauTextsContentSchema,
  noyauTextsDraftContentSchema,
} from '@matter_extractor/shared';

/**
 * What Réglages > Noyaux holds while somebody types (noyau.md §6, issue #148).
 *
 * The comparison that greys « Enregistrer », the version numbering and the
 * three refusals are the layer's (`versioned-draft.ts`). What is here is the
 * SHAPE of the texts' draft and the functions that cross between it and the
 * wire - `VersionedDraftKit` - plus the two counts the screen shows.
 *
 * ## The draft IS the draft content
 *
 * The criterion keeps a looser shape of its own because a draft there is
 * string fields and a list; here every field is already a rich text, and the
 * shared draft schema already says the one thing a draft adds: a text may be
 * EMPTY (N7). So the editor holds `NoyauTextsDraftContent` as it is, and the
 * version is what `noyauTextsContentSchema` accepts of it - the schema is
 * asked rather than re-decided, the way the criterion asks it of its scoring.
 */

export type NoyauTextsDraft = NoyauTextsDraftContent;

/** The three texts of one noyau, in the order of §6.2 and of the card. */
export const NOYAU_TEXT_FIELDS = ['firstText', 'secondText', 'portrait'] as const;
export type NoyauTextField = (typeof NOYAU_TEXT_FIELDS)[number];

/** Eighteen: six noyaux, three texts each (§6.2). */
export const NOYAU_TEXT_TOTAL = NOYAU_CODES.length * NOYAU_TEXT_FIELDS.length;

/** The texts nobody has typed: every one of the eighteen empty, the fixed name. */
export const EMPTY_NOYAU_TEXTS_DRAFT: NoyauTextsDraft = noyauTextsDraftContentSchema.parse({});

/** How many of the three texts of one noyau are written - « 2 sur 3 » (§6.3). */
export function filledCountOf(entry: NoyauTextEntryDraft): number {
  return NOYAU_TEXT_FIELDS.filter((field) => noyauTextFilled(entry[field])).length;
}

/** How many of the eighteen are written - « 7 sur 18 remplis » (§6.3). */
export function totalFilledOf(draft: NoyauTextsDraft): number {
  return NOYAU_CODES.reduce((sum, code) => sum + filledCountOf(draft.noyaux[code]), 0);
}

/** The draft with ONE text rewritten, and everything else shared by reference. */
export function withText(
  draft: NoyauTextsDraft,
  code: NoyauCode,
  field: NoyauTextField,
  text: RichText,
): NoyauTextsDraft {
  return {
    ...draft,
    noyaux: { ...draft.noyaux, [code]: { ...draft.noyaux[code], [field]: text } },
  };
}

/**
 * What the tab opens on: the server draft when there is one, the
 * `développement` version otherwise, and the eighteen empty texts while no
 * version exists (§6.4), until the first save writes the entity and its v1
 * (N8). Before that first save the layer seeds from what THIS browser kept
 * first, when it kept something (`lib/noyau-texts-browser-draft.ts`): this is
 * the opening of a browser that kept nothing.
 */
export function openingDraft(detail: NoyauTextsDetail | null): NoyauTextsDraft {
  if (detail === null) return EMPTY_NOYAU_TEXTS_DRAFT;
  return detail.draft?.content ?? detail.developmentContent;
}

/**
 * The version a draft would become, or `null` while one of the eighteen texts
 * is empty or over its bound - « Enregistrer une nouvelle version » exige les
 * dix-huit textes non vides (N7). The shared schema decides, so the button and
 * the server refuse the same thing.
 */
export function normalizedContent(draft: NoyauTextsDraft): NoyauTextsContent | null {
  const parsed = noyauTextsContentSchema.safeParse(draft);
  return parsed.success ? parsed.data : null;
}

export const NOYAU_TEXTS_DRAFT_KIT: VersionedDraftKit<
  NoyauTextsContent,
  NoyauTextsDraftContent,
  NoyauTextsDraft
> = {
  opening: openingDraft,
  fromContent: (content) => content,
  normalized: normalizedContent,
  payload: (draft) => draft,
};
